import {
  ScmIntegrationsApi,
  scmIntegrationsApiRef,
  ScmAuth,
} from '@backstage/integration-react';
import {
  ApiRef,
  BackstageIdentityApi,
  OpenIdConnectApi,
  ProfileInfoApi,
  SessionApi,
  configApiRef,
  createApiRef,
  discoveryApiRef,
  oauthRequestApiRef,
} from '@backstage/core-plugin-api';
import { OAuth2 } from '@backstage/core-app-api';
import { ApiBlueprint, createFrontendModule } from '@backstage/frontend-plugin-api';
import { TechRadarClient } from './lib/tech-radar-client';
import { techRadarApiRef } from '@backstage-community/plugin-tech-radar';
import { costInsightsApiRef } from '@backstage-community/plugin-cost-insights';
import { CostInsightsClient } from './lib/CostInsightsClient';
import { createBatchedCatalogApi } from './lib/batchedCatalogApi';
import {
  catalogApiRef,
  EntityPresentationApi,
  entityPresentationApiRef,
} from '@backstage/plugin-catalog-react';
import { SimpleIconsEntityPresentationApi } from '@dweber019/backstage-plugin-simple-icons';

export const azureOIDCAuthApiRef: ApiRef<
  OpenIdConnectApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
> = createApiRef({
  id: 'auth.azure.oidc',
});

export const apisModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    ApiBlueprint.make({
      name: 'scm-integrations',
      params: defineParams =>
        defineParams({
          api: scmIntegrationsApiRef,
          deps: { configApi: configApiRef },
          factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
        }),
    }),
    ApiBlueprint.make({
      name: 'scm-auth',
      params: defineParams => defineParams(ScmAuth.createDefaultApiFactory()),
    }),
    ApiBlueprint.make({
      name: 'azure-oidc-auth',
      params: defineParams =>
        defineParams({
          api: azureOIDCAuthApiRef,
          deps: {
            discoveryApi: discoveryApiRef,
            oauthRequestApi: oauthRequestApiRef,
            configApi: configApiRef,
          },
          factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
            OAuth2.create({
              discoveryApi,
              oauthRequestApi,
              provider: {
                id: 'azureoidc',
                title: 'Azure OIDC Provider',
                icon: () => null,
              },
              environment: configApi.getOptionalString('auth.environment'),
              defaultScopes: ['openid', 'profile', 'email'],
              popupOptions: {
                size: {
                  width: 500,
                  height: 500,
                },
              },
            }),
        }),
    }),
    ApiBlueprint.make({
      name: 'tech-radar',
      params: defineParams =>
        defineParams({
          api: techRadarApiRef,
          deps: { configApi: configApiRef },
          factory: ({ configApi }) => new TechRadarClient(configApi),
        }),
    }),
    ApiBlueprint.make({
      name: 'cost-insights',
      params: defineParams =>
        defineParams({
          api: costInsightsApiRef,
          deps: { configApi: configApiRef },
          factory: ({ configApi }) => new CostInsightsClient(configApi),
        }),
    }),
    // The legacy `catalog` plugin also provides this apiRef, and in the new
    // frontend system two plugins providing the same API is a startup error
    // rather than an app-level override. Theirs is switched off via
    // `app.extensions` in app-config.yaml, under the id the compat shim gives
    // it: `api:catalog/plugin.catalog.entity-presentation`. Once catalog is
    // consumed as a native NFS plugin that id becomes
    // `api:catalog/entity-presentation` and the key stops matching -- which
    // fails loudly, since the conflict returns and the app refuses to start.
    ApiBlueprint.make({
      name: 'entity-presentation',
      params: defineParams =>
        defineParams({
          api: entityPresentationApiRef,
          deps: { catalogApi: catalogApiRef },
          factory: ({ catalogApi }) => {
            // SimpleIcons calls catalogApi.getEntityByRef once per rendered
            // ref (no caching, no batching), flooding the backend with by-name
            // lookups on every catalog page render and kind switch.
            return SimpleIconsEntityPresentationApi.create({
              catalogApi: createBatchedCatalogApi(catalogApi),
            }) as EntityPresentationApi;
          },
        }),
    }),
  ],
});
